import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import {
    WMS2010_R01StockDto,
    WMS2010_R01ItemSubtotalData,
    WMS2010_R01WarehouseSubtotalData,
    WMS2010_R01LogicCenterSubtotalData,
    WMS2010_R01Dto,
} from './Dto_WMS2010_R01';

// 탭 타입 정의 및 UI 상태 타입
export type TabType = 'item' | 'warehouse' | 'logiccenter' | 'total' | 'lot';

export interface TabConfig {
    id: TabType;
    label: string;
    description: string;
}

export type ItemStockData = WMS2010_R01StockDto;
export type WarehouseStockData = WMS2010_R01StockDto;
export type LogicCenterStockData = WMS2010_R01StockDto;
export type TotalStockData = WMS2010_R01StockDto;
export type LotStockData = WMS2010_R01StockDto;

export interface TabData {
    item: ItemStockData[];
    warehouse: WarehouseStockData[];
    logiccenter: LogicCenterStockData[];
    total: TotalStockData[];
    lot: LotStockData[];
}

// 소계 데이터 인터페이스
export interface TabSubtotalData {
    item: WMS2010_R01ItemSubtotalData[];
    warehouse: WMS2010_R01WarehouseSubtotalData[];
    logiccenter: WMS2010_R01LogicCenterSubtotalData[];
    total: any[]; // 전체 탭은 소계 없음
    lot: any[]; // LOT 탭은 소계 없음
}

// 그리드 데이터 인터페이스 (소계 포함)
export interface TabGridData {
    item: WMS2010_R01Dto[];
    warehouse: WMS2010_R01Dto[];
    logiccenter: WMS2010_R01Dto[];
    total: WMS2010_R01Dto[];
    lot: WMS2010_R01Dto[];
}

export interface TabColumnStates {
    item: ColumnState[];
    warehouse: ColumnState[];
    logiccenter: ColumnState[];
    total: ColumnState[];
    lot: ColumnState[];
}

export interface TabLoadingStates {
    item: boolean;
    warehouse: boolean;
    logiccenter: boolean;
    total: boolean;
    lot: boolean;
}

// 탭별 선택된 행 ID 목록 (다중 선택)
export interface TabSelectedIds {
    item: string[];
    warehouse: string[];
    logiccenter: string[];
    total: string[];
    lot: string[];
}

export interface TabScrollTop {
    item: number | null;
    warehouse: number | null;
    logiccenter: number | null;
    total: number | null;
    lot: number | null;
}

const defaultTabScrollTop = (): TabScrollTop => ({
    item: null,
    warehouse: null,
    logiccenter: null,
    total: null,
    lot: null,
});

// 필터 인터페이스 (API에서 사용)
export interface WMS2010_R01Filter {
    logicCenter: string;
    warehouseCd: string;
    itemCd: string;
	itemNm: string;
    itemGroup: string;
    brandCd: string;
}

export const defaultWMS2010_R01Filter: WMS2010_R01Filter = {
    logicCenter: '',
    warehouseCd: '',
    itemCd: '',
    itemNm: '',
    itemGroup: '',
    brandCd: ''
};

interface WMS2010_R01Store {
    // 기본 필터
    WMS2010_R01Filter: WMS2010_R01Filter;
    setWMS2010_R01Filter: (filter: Partial<WMS2010_R01Filter>) => void;
    
    
    // 활성 탭
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    
    // 탭별 데이터
    tabData: TabData;
    setTabData: (tabType: TabType, data: any[]) => void;
    
    // 탭별 컬럼 상태
    tabColumnStates: TabColumnStates;
    setTabColumnState: (tabType: TabType, columnState: any[]) => void;
    
    // 탭별 로딩 상태
    tabLoadingStates: TabLoadingStates;
    setTabLoadingState: (tabType: TabType, loading: boolean) => void;
    
    // 탭별 선택 상태 (다중 선택)
    tabSelectedIds: TabSelectedIds;
    setTabSelectedId: (tabType: TabType, ids: string[]) => void;
    clearTabSelectedIds: (tabType: TabType) => void;

    tabScrollTop: TabScrollTop;
    setTabScrollTop: (tabType: TabType, top: number | null) => void;
    resetAllTabScrollTop: () => void;
    
    // === 소계 관련 상태 ===
    showSubtotals: boolean;
    setShowSubtotals: (show: boolean) => void;
    
    // 탭별 소계 데이터
    tabSubtotalData: TabSubtotalData;
    setTabSubtotalData: (tabType: TabType, data: any[]) => void;
    
    // 탭별 그리드 데이터 (소계 포함)
    tabGridData: TabGridData;
    setTabGridData: (tabType: TabType, data: WMS2010_R01Dto[]) => void;
    
    // 초기화
    initData: () => void;
}

export const useWMS2010_R01Store = create<WMS2010_R01Store>((set, get) => ({
    // 기본 필터
    WMS2010_R01Filter: defaultWMS2010_R01Filter,
    setWMS2010_R01Filter: (filter) => set((state) => ({
        WMS2010_R01Filter: {  ...defaultWMS2010_R01Filter, ...state.WMS2010_R01Filter, ...filter }
    })),
    
    // 활성 탭
    activeTab: 'total',
    setActiveTab: (tab) => {
        set({ activeTab: tab });
    },
    
    // 탭별 데이터
    tabData: {
        total: [],
        item: [],
        warehouse: [],
        logiccenter: [],
        lot: []
    },
    setTabData: (tabType, data) => set((state) => ({
        tabData: {
            ...state.tabData,
            [tabType]: data
        }
    })),
    
    // 탭별 컬럼 상태
    tabColumnStates: {
        total: [],
        item: [],
        warehouse: [],
        logiccenter: [],
        lot: []
    },
    setTabColumnState: (tabType, columnState) => set((state) => ({
        tabColumnStates: {
            ...state.tabColumnStates,
            [tabType]: columnState
        }
    })),
    
    // 탭별 로딩 상태
    tabLoadingStates: {
        total: false,
        item: false,
        warehouse: false,
        logiccenter: false,
        lot: false
    },
    setTabLoadingState: (tabType, loading) => set((state) => ({
        tabLoadingStates: {
            ...state.tabLoadingStates,
            [tabType]: loading
        }
    })),
    
    // 탭별 선택 상태 (다중 선택)
    tabSelectedIds: {
        total: [],
        item: [],
        warehouse: [],
        logiccenter: [],
        lot: []
    },
    // 탭별 선택 ID 배열을 통째로 교체(일반 클릭 토글/Shift 범위 선택 결과 반영)
    setTabSelectedId: (tabType, ids) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: ids
        }
    })),
    clearTabSelectedIds: (tabType) => set((state) => ({
        tabSelectedIds: {
            ...state.tabSelectedIds,
            [tabType]: []
        }
    })),

    tabScrollTop: defaultTabScrollTop(),
    setTabScrollTop: (tabType, top) => set((state) => ({
        tabScrollTop: {
            ...state.tabScrollTop,
            [tabType]: top,
        },
    })),
    resetAllTabScrollTop: () => set({ tabScrollTop: defaultTabScrollTop() }),
    
    // === 소계 관련 상태 ===
    showSubtotals: false,
    setShowSubtotals: (show) => set({ showSubtotals: show }),
    
    // 탭별 소계 데이터
    tabSubtotalData: {
        item: [],
        warehouse: [],
        logiccenter: [],
        total: [],
        lot: [],
    },
    setTabSubtotalData: (tabType, data) => set((state) => ({
        tabSubtotalData: {
            ...state.tabSubtotalData,
            [tabType]: data
        }
    })),
    
    // 탭별 그리드 데이터 (소계 포함)
    tabGridData: {
        item: [],
        warehouse: [],
        logiccenter: [],
        total: [],
        lot: [],
    },
    setTabGridData: (tabType, data) => set((state) => ({
        tabGridData: {
            ...state.tabGridData,
            [tabType]: data
        }
    })),
    
    // 초기화
    initData: () => set({
        WMS2010_R01Filter: defaultWMS2010_R01Filter,
        activeTab: 'total',
        tabData: {
            total: [],
            item: [],
            warehouse: [],
            logiccenter: [],
            lot: []
        },
        tabColumnStates: {
            total: [],
            item: [],
            warehouse: [],
            logiccenter: [],
            lot: []
        },
        tabLoadingStates: {
            total: false,
            item: false,
            warehouse: false,
            logiccenter: false,
            lot: false
        },
        tabSelectedIds: {
            total: [],
            item: [],
            warehouse: [],
            logiccenter: [],
            lot: []
        },
        showSubtotals: false,
        tabSubtotalData: {
            item: [],
            warehouse: [],
            logiccenter: [],
            total: [],
            lot: [],
        },
        tabGridData: {
            item: [],
            warehouse: [],
            logiccenter: [],
            total: [],
            lot: [],
        },
        tabScrollTop: defaultTabScrollTop(),
    }),
}));

export default useWMS2010_R01Store;
